import { useEffect, useRef, useState } from 'react';

import {
  normalizeStreamStatus,
  STATUS_DUPLICATE_WINDOW_MS,
  STATUS_MIN_VISIBLE_MS,
  type StreamStatus,
} from '../constants';

const FIRST_STATUS: StreamStatus = { key: 'ai.received', text: 'Mottaget' };
const CLEAR_DELAY_MS = 2000;
const LINE_HISTORY = 4;

export const useStreamStatus = () => {
  const [activeStreamStatus, setActiveStreamStatus] = useState<StreamStatus | null>(null);
  const [streamStatusLines, setStreamStatusLines] = useState<string[]>([]);
  const clearTimeoutRef = useRef<number | null>(null);
  const deferredTimeoutRef = useRef<number | null>(null);
  const lastStatusRef = useRef<{ key: string; text: string; at: number } | null>(null);
  const shownAtRef = useRef<number>(0);

  const cancelClearTimer = () => {
    if (clearTimeoutRef.current !== null) {
      window.clearTimeout(clearTimeoutRef.current);
      clearTimeoutRef.current = null;
    }
  };

  const cancelDeferredTimer = () => {
    if (deferredTimeoutRef.current !== null) {
      window.clearTimeout(deferredTimeoutRef.current);
      deferredTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      cancelClearTimer();
    };
  }, []);

  const beginStream = () => {
    const first = normalizeStreamStatus(FIRST_STATUS);
    setActiveStreamStatus(first);
    lastStatusRef.current = { key: first.key, text: first.text, at: Date.now() };
    cancelClearTimer();
    cancelDeferredTimer();
    shownAtRef.current = Date.now();
    setStreamStatusLines([first.text]);
  };

  // Ger tillbaka statusen som togs emot, eller null när den kvävdes som dubblett.
  // Anroparen hänger sina egna sidoeffekter på returvärdet — en kvävd status ska
  // inte flagga meddelandet som "bygger outfit" heller.
  const pushStatus = (status: { key: string; text: string }): StreamStatus | null => {
    const normalizedStatus = normalizeStreamStatus(status);
    const nextText = normalizedStatus.text.trim();
    if (!nextText) {
      return null;
    }

    const now = Date.now();
    const previousStatus = lastStatusRef.current;
    if (
      previousStatus &&
      previousStatus.key === normalizedStatus.key &&
      previousStatus.text === nextText &&
      now - previousStatus.at < STATUS_DUPLICATE_WINDOW_MS
    ) {
      return null;
    }
    lastStatusRef.current = { key: normalizedStatus.key, text: nextText, at: now };

    const applyStatus = () => {
      setActiveStreamStatus(normalizedStatus);
      shownAtRef.current = Date.now();
      deferredTimeoutRef.current = null;
    };
    setActiveStreamStatus((current) => {
      if (current?.key === normalizedStatus.key && current?.text === nextText) {
        return current;
      }
      const elapsed = now - shownAtRef.current;
      if (!current || elapsed >= STATUS_MIN_VISIBLE_MS) {
        shownAtRef.current = now;
        return normalizedStatus;
      }
      cancelDeferredTimer();
      deferredTimeoutRef.current = window.setTimeout(applyStatus, STATUS_MIN_VISIBLE_MS - elapsed);
      return current;
    });
    setStreamStatusLines((prev) => {
      if (prev[prev.length - 1] === nextText) {
        return prev;
      }
      return [...prev, nextText].slice(-LINE_HISTORY);
    });

    return normalizedStatus;
  };

  const endStream = () => {
    cancelDeferredTimer();
    clearTimeoutRef.current = window.setTimeout(() => {
      setActiveStreamStatus(null);
      setStreamStatusLines([]);
      clearTimeoutRef.current = null;
    }, CLEAR_DELAY_MS);
  };

  const resetStatus = () => {
    setStreamStatusLines([]);
    cancelClearTimer();
  };

  return {
    activeStreamStatus,
    streamStatusLines,
    beginStream,
    pushStatus,
    endStream,
    resetStatus,
  };
};
